/* ==========================================================
   clinic-finder.js — product-page "nearest clinic" finder
   Scoped to [data-clinic-finder]; no jQuery dependency.

   Modes (set by the section in data-mode):
     book   — product has an in-clinic variant + appointment type id.
              Clinics come from the booking API (only ones bookable for this
              test), enriched with Shopify hours/URL. "Book here" writes the
              same sessionStorage keys the product form + /pages/find-clinic
              write, then goes straight to /pages/get-calendar-dates.
     browse — no appointment type (e.g. MRI). Distances calculated locally
              over the product's Shopify location collections; CTA opens the
              clinic page.
   ========================================================== */
(function () {
  'use strict';

  var API_BY_LOCATION = 'https://api.gbc.health/api/v2/shopify-get-clinics-by-location';
  var CAPTURE_URL     = 'https://partners.goodbodyclinic.com/api/coverage/capture';
  var CALENDAR_PAGE   = '/pages/get-calendar-dates';
  var LAST_KEY        = 'gb_cf_last_search';
  var CARTO_KEY       = 'cb1_2bg3_1_86b52f35b3800e0090776f0d';
  var DESKTOP_MQ      = window.matchMedia('(min-width: 900px)');

  /* ── utils ── */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  // Customer-facing clinic name: "Warminster Clinic - Ear to Clear" -> "Warminster Clinic".
  // The host-business suffix stays in c.name for sessionStorage/tracking (ops need it).
  function shortName(s) {
    var t = String(s == null ? '' : s);
    var m = t.match(/^(.*?\S)\s+(?:-|\u2013|\u2014|\|)\s+/);
    return m ? m[1] : t;
  }
  function miles(lat1, lng1, lat2, lng2) {
    var R = 3958.8, p = Math.PI / 180;
    var a = Math.sin((lat2 - lat1) * p / 2), b = Math.sin((lng2 - lng1) * p / 2);
    var h = a * a + Math.cos(lat1 * p) * Math.cos(lat2 * p) * b * b;
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  function fmtDist(d) {
    if (typeof d !== 'number') return '';
    if (d < 0.3) return 'Under half a mile';
    return d.toFixed(1) + ' miles';
  }
  function looksLikePostcode(pc) { return /^[A-Z]{1,2}\d[A-Z\d]?(\d[A-Z]{2})?$/.test(pc); }
  function outward(pc) {
    var m = pc.toUpperCase().replace(/\s+/g, '').match(/^([A-Z]{1,2}\d[A-Z\d]?)/);
    return m ? m[1] : pc;
  }

  /* ── opening hours (mirrors locations-finder.js) ── */
  var DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  function fmtTime(t) {
    if (!t) return '';
    var parts = t.split(':'), h = parseInt(parts[0], 10), m = parts[1] || '00';
    var suffix = h >= 12 ? 'pm' : 'am'; h = h % 12 || 12;
    return h + (m === '00' ? '' : ':' + m) + suffix;
  }
  function todayHours(hours) {
    if (!hours || typeof hours !== 'object') return { state: 'unknown', text: 'Hours on clinic page' };
    var now = new Date(), day = DAYS[now.getDay()], today = hours[day];
    if (!today || !today.open) {
      // find next open day
      for (var i = 1; i <= 7; i++) {
        var d = hours[DAYS[(now.getDay() + i) % 7]];
        if (d && d.open) return { state: 'closed', text: 'Closed today · opens ' + (i === 1 ? 'tomorrow' : DAYS[(now.getDay() + i) % 7].slice(0, 1).toUpperCase() + DAYS[(now.getDay() + i) % 7].slice(1, 3)) + ' ' + fmtTime(d.open) };
      }
      return { state: 'closed', text: 'Closed today' };
    }
    var mins = now.getHours() * 60 + now.getMinutes();
    var o = today.open.split(':'), c = today.close.split(':');
    var om = +o[0] * 60 + +o[1], cm = +c[0] * 60 + +c[1];
    if (mins >= om && mins < cm) return { state: 'open', text: 'Open today until ' + fmtTime(today.close) };
    if (mins < om) return { state: 'later', text: 'Opens today at ' + fmtTime(today.open) };
    return { state: 'closed', text: 'Closed now · today ' + fmtTime(today.open) + '–' + fmtTime(today.close) };
  }

  /* ── geocoding: full postcode → outward code → place name ── */
  var PLACE_RANK = { 'City': 0, 'Town': 1, 'Suburban Area': 2, 'Village': 3, 'Hamlet': 4 };
  function geocode(raw) {
    var q = raw.trim(), pc = q.toUpperCase().replace(/\s+/g, '');
    function place() {
      return fetch('https://api.postcodes.io/places?q=' + encodeURIComponent(q) + '&limit=10')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var list = (d.status === 200 && d.result) ? d.result.slice() : [];
          if (!list.length) throw new Error('We couldn’t find that postcode or town. Please check and try again.');
          var ql = q.toLowerCase();
          list.sort(function (a, b) {
            var ea = (a.name_1 || '').toLowerCase() === ql ? 0 : 1, eb = (b.name_1 || '').toLowerCase() === ql ? 0 : 1;
            if (ea !== eb) return ea - eb;
            return (PLACE_RANK[a.local_type] !== undefined ? PLACE_RANK[a.local_type] : 9) - (PLACE_RANK[b.local_type] !== undefined ? PLACE_RANK[b.local_type] : 9);
          });
          return { lat: list[0].latitude, lng: list[0].longitude, label: list[0].name_1 || q, kind: 'place' };
        });
    }
    if (!looksLikePostcode(pc)) return place();
    return fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(pc))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d.status === 200) return { lat: d.result.latitude, lng: d.result.longitude, label: q, kind: 'postcode' };
        return fetch('https://api.postcodes.io/outcodes/' + encodeURIComponent(pc.replace(/\d[A-Z]{2}$/, '')))
          .then(function (r2) { return r2.json(); })
          .then(function (d2) {
            if (d2.status === 200) return { lat: d2.result.latitude, lng: d2.result.longitude, label: q, kind: 'outcode' };
            return place();
          });
      });
  }

  /* ── inline icons for JS-rendered cards (currentColor, sized by CSS) ── */
  var ICON = {
    pin:   '<svg viewBox="0 0 45 68" xmlns="http://www.w3.org/2000/svg"><path d="M22.5 67.36C15.19 55.67-.29 36.09 0 23.24.29 10.67 9.94.74 22.5.74c12.27 0 22.21 9.93 22.5 22.5.29 12.85-15.19 32.43-22.5 44.12Z"/></svg>',
    walk:  '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M8 1.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm-1.6 5.2 1.9-.6c.9-.3 1.8.2 2.1 1l.8 2.1 1.9 1a.75.75 0 1 1-.7 1.3l-2.2-1.1a.9.9 0 0 1-.4-.5l-.5-1.2-.9 3 2 1.9c.2.2.3.5.3.8l-.4 2.9a.75.75 0 1 1-1.5-.2l.3-2.5-2-1.9-.6 1.8-2 2.3a.75.75 0 1 1-1.1-1l1.8-2.1L6.3 9l-.8.3-.7 2.1a.75.75 0 1 1-1.4-.5l.8-2.4c.2-.5.6-.9 1.1-1.1l1.1-.7Z"/></svg>',
    clock: '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1Zm0 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Zm-.75 2.25a.75.75 0 0 1 1.5 0V7.7l2.03 1.35a.75.75 0 1 1-.83 1.25L7.6 8.72a.75.75 0 0 1-.35-.63V4.75Z"/></svg>',
    arrow: '<svg viewBox="0 0 38 32" xmlns="http://www.w3.org/2000/svg"><path d="M23.29 31.37 37.54 17.12a1.58 1.58 0 0 0 0-2.24L23.29.63a1.58 1.58 0 0 0-2.24 2.24L32.6 14.42H1.58a1.58 1.58 0 0 0 0 3.16H32.6L21.05 29.13a1.58 1.58 0 0 0 2.24 2.24Z"/></svg>',
    info:  '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1Zm0 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Zm0 4.25a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 6.75ZM8 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/></svg>',
    dir:   '<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M7.3.7a1 1 0 0 1 1.4 0l6.6 6.6a1 1 0 0 1 0 1.4l-6.6 6.6a1 1 0 0 1-1.4 0L.7 8.7a1 1 0 0 1 0-1.4L7.3.7ZM8 2.4 2.4 8 8 13.6 13.6 8 8 2.4Zm.9 3.1 2 2a.5.5 0 0 1 0 .7l-2 2a.5.5 0 0 1-.85-.35V8.75H6.5V10.5h-1V8.25a.5.5 0 0 1 .5-.5h2.05V6.5a.5.5 0 0 1 .85-.35Z"/></svg>'
  };

  /* ── component ── */
  function ClinicFinder(root) {
    this.root = root;
    this.mode = root.dataset.mode || 'browse';
    this.limit = parseInt(root.dataset.limit, 10) || 5;
    this.farMiles = parseInt(root.dataset.farMiles, 10) || 25;
    this.visible = this.limit;
    this.results = [];
    this.origin = null;
    this.map = null;
    this.markers = [];

    var sectionId = root.closest('.cf-section').id.replace('clinic-finder-', '');
    var dataEl = document.querySelector('script[data-cf-clinics="' + sectionId + '"]');
    try { this.shopClinics = dataEl ? JSON.parse(dataEl.textContent) : []; } catch (e) { this.shopClinics = []; }

    this.els = {
      form: $('[data-cf-form]', root), input: $('[data-cf-input]', root), submit: $('[data-cf-submit]', root),
      submitLabel: $('[data-cf-submit-label]', root), geo: $('[data-cf-geo]', root), error: $('[data-cf-error]', root),
      facts: $('[data-cf-facts]', root), results: $('[data-cf-results]', root), summary: $('[data-cf-summary]', root),
      change: $('[data-cf-change]', root), far: $('[data-cf-far]', root), list: $('[data-cf-list]', root),
      more: $('[data-cf-more]', root), mapCol: $('[data-cf-map-col]', root), mapEl: $('[data-cf-map]', root)
    };
    this.els.submitLabel.dataset.label = this.els.submitLabel.textContent;
    this.bind();
    this.prefill();
    // Desktop: show the whole network for this test as small dots before any search
    var self = this;
    if (DESKTOP_MQ.matches) this.renderOverview();
    var onMq = function () { if (DESKTOP_MQ.matches && !self.origin) self.renderOverview(); };
    if (DESKTOP_MQ.addEventListener) DESKTOP_MQ.addEventListener('change', onMq);
  }

  var UK_BOUNDS = [[49.9, -8.2], [58.7, 1.8]];
  ClinicFinder.prototype.ensureMap = function (L) {
    var e = this.els;
    if (this.map) return this.map;
    this.map = L.map(e.mapEl, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=' + CARTO_KEY, {
      subdomains: 'abcd', maxZoom: 19, attribution: '&copy; OpenStreetMap &copy; CARTO'
    }).addTo(this.map);
    return this.map;
  };
  ClinicFinder.prototype.renderOverview = function () {
    var self = this, e = this.els;
    if (!this.shopClinics.length) return;
    e.mapCol.hidden = false;
    var note = e.mapCol.querySelector('.cf-map__note');
    if (note) note.textContent = this.shopClinics.length + ' clinics offer this test \u2014 search to find your nearest';
    loadLeaflet(function (L) {
      var map = self.ensureMap(L);
      if (!self.dots) {
        self.dots = L.layerGroup();
        self.shopClinics.forEach(function (c) {
          if (isNaN(c.lat) || isNaN(c.lng)) return;
          L.circleMarker([c.lat, c.lng], { radius: 3.5, color: '#fff', weight: 1, fillColor: '#52B27F', fillOpacity: 0.9, interactive: false })
            .addTo(self.dots);
        });
      }
      self.dots.addTo(map);
      setTimeout(function () { map.invalidateSize(); if (!self.origin) map.fitBounds(UK_BOUNDS, { padding: [10, 10] }); }, 60);
    });
  };

  ClinicFinder.prototype.bind = function () {
    var self = this, e = this.els;
    e.form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var v = e.input.value.trim();
      self.error('');
      if (!v) { self.error('Please enter a postcode or town.'); e.input.focus(); return; }
      self.loading(true);
      geocode(v).then(function (g) { self.search(g.lat, g.lng, g.label, g.kind); })
        .catch(function (err) { self.error(err.message || 'Could not reach the location service. Please try again.'); })
        .then(function () { self.loading(false); });
    });
    e.geo.addEventListener('click', function () {
      self.error('');
      if (!navigator.geolocation) { self.error('Location is not available in this browser. Please enter a postcode.'); return; }
      var lbl = e.geo.querySelector('[data-cf-geo-label]') || e.geo;
      var label = lbl.textContent; lbl.textContent = 'Locating…'; e.geo.disabled = true;
      navigator.geolocation.getCurrentPosition(function (pos) {
        lbl.textContent = label; e.geo.disabled = false;
        self.search(pos.coords.latitude, pos.coords.longitude, 'your location', 'geo');
      }, function () {
        lbl.textContent = label; e.geo.disabled = false;
        self.error('We couldn’t access your location. Please enter a postcode instead.');
      }, { timeout: 8000 });
    });
    // Tidy postcodes as people type: "ba11 6te" → "BA11 6TE"
    e.input.addEventListener('blur', function () {
      var pc = e.input.value.trim().toUpperCase().replace(/\s+/g, '');
      if (looksLikePostcode(pc)) e.input.value = pc.length > 4 ? pc.slice(0, -3) + ' ' + pc.slice(-3) : pc;
    });
    e.change.addEventListener('click', function () { e.input.focus(); e.input.select(); e.input.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
    e.more.addEventListener('click', function () { self.visible += self.limit; self.render(); });
  };

  ClinicFinder.prototype.prefill = function () {
    try {
      var last = JSON.parse(localStorage.getItem(LAST_KEY) || 'null');
      if (last && last.label && last.kind !== 'geo') this.els.input.value = last.label;
    } catch (e) { /* storage unavailable */ }
  };

  ClinicFinder.prototype.error = function (msg) {
    this.els.error.textContent = msg; this.els.error.hidden = !msg;
  };
  ClinicFinder.prototype.loading = function (on) {
    this.els.submit.disabled = on;
    this.els.submitLabel.textContent = on ? 'Searching…' : this.els.submitLabel.dataset.label;
  };

  /* ── search ── */
  ClinicFinder.prototype.search = function (lat, lng, label, kind) {
    var self = this;
    this.origin = { lat: lat, lng: lng, label: label, kind: kind };
    this.visible = this.limit;
    try { localStorage.setItem(LAST_KEY, JSON.stringify({ label: label, kind: kind })); } catch (e) { /* ignore */ }

    var local = this.localResults(lat, lng);
    var done = function (list, source) {
      self.results = list;
      self.render();
      self.beacon(source);
      self.track('clinic_finder_search', { kind: kind, results: list.length, nearest_mi: list[0] ? Math.round(list[0].dist * 10) / 10 : null });
    };

    if (this.mode !== 'book') { done(local, 'shopify'); return; }

    this.loading(true);
    var url = API_BY_LOCATION + '?coord_lat=' + lat + '&coord_lng=' + lng + '&appointmentTypeId=' + encodeURIComponent(this.root.dataset.aptId);
    fetch(url).then(function (r) { if (!r.ok) throw new Error('api'); return r.json(); })
      .then(function (api) {
        if (!Array.isArray(api)) throw new Error('api');
        // An empty array is a real answer (nothing within the API's 100-mile radius), not a failure
        var list = api.map(function (c) {
          var clat = parseFloat(c.lat_long && c.lat_long[0]), clng = parseFloat(c.lat_long && c.lat_long[1]);
          var match = self.nearestShop(clat, clng);
          return {
            name: c.name, address: c.address, dist: c.distance, lat: clat, lng: clng,
            calendarId: c.calendarId, bookable: true,
            hours: match ? match.h : null, url: match ? match.u : null
          };
        }).sort(function (a, b) { return a.dist - b.dist; });
        done(list, 'api');
      })
      .catch(function () { done(local, 'shopify-fallback'); })
      .then(function () { self.loading(false); });
  };

  ClinicFinder.prototype.localResults = function (lat, lng) {
    return this.shopClinics.map(function (c) {
      return { name: c.t, address: c.a, dist: miles(lat, lng, c.lat, c.lng), lat: c.lat, lng: c.lng, hours: c.h, url: c.u, bookable: false };
    }).sort(function (a, b) { return a.dist - b.dist; });
  };

  ClinicFinder.prototype.nearestShop = function (lat, lng) {
    if (isNaN(lat) || isNaN(lng)) return null;
    var best = null, bestD = 0.25; // same site if within a quarter mile
    for (var i = 0; i < this.shopClinics.length; i++) {
      var d = miles(lat, lng, this.shopClinics[i].lat, this.shopClinics[i].lng);
      if (d < bestD) { bestD = d; best = this.shopClinics[i]; }
    }
    return best;
  };

  /* ── render ── */
  ClinicFinder.prototype.render = function () {
    var e = this.els, list = this.results, o = this.origin;
    e.facts.hidden = true;
    e.results.hidden = false;

    if (!list.length) {
      e.summary.textContent = 'We couldn’t find a clinic offering this test within 100 miles of ' + o.label + '.';
      e.far.hidden = false;
      e.far.innerHTML = 'You can still take this test with a <strong>home nurse visit</strong> or a <strong>finger-prick kit</strong> — choose an option at the top of this page.';
      e.list.innerHTML = ''; e.more.hidden = true; e.mapCol.hidden = true;
      return;
    }

    var nearest = list[0].dist;
    e.summary.innerHTML = '<strong>Nearest clinics to ' + esc(o.label) + '</strong> · closest ' + fmtDist(nearest).toLowerCase() + ' · ' + list.length + ' within reach';

    if (nearest > this.farMiles) {
      e.far.hidden = false;
      e.far.innerHTML = 'Your nearest clinic is ' + fmtDist(nearest).toLowerCase() + ' away. A <strong>home nurse visit</strong> or <strong>finger-prick kit</strong> may suit you better — both are options at the top of this page.';
    } else {
      e.far.hidden = true;
    }

    var self = this, frag = document.createDocumentFragment();
    list.slice(0, this.visible).forEach(function (c, i) {
      var h = todayHours(c.hours);
      var li = document.createElement('li');
      li.className = 'cf-card' + (i === 0 ? ' cf-card--nearest' : '');
      li.innerHTML =
        '<div class="cf-card__top">' +
          '<span class="cf-card__pin" aria-hidden="true">' + ICON.pin + '<span class="cf-card__pin-num">' + (i + 1) + '</span></span>' +
          '<div class="cf-card__body">' +
            '<h3 class="cf-card__name">' + esc(shortName(c.name)) + (i === 0 ? '<span class="cf-badge">Nearest</span>' : '') + '</h3>' +
            '<p class="cf-card__meta"><span class="cf-card__dist">' + ICON.walk + fmtDist(c.dist) + '</span>' +
              '<span class="cf-card__hours cf-card__hours--' + h.state + '">' + ICON.clock + esc(h.text) + '</span></p>' +
            '<p class="cf-card__addr">' + esc(c.address) + '</p>' +
          '</div>' +
        '</div>' +
        '<div class="cf-card__actions">' +
          (c.bookable
            ? '<button type="button" class="cf-btn cf-btn--book" data-cf-book="' + i + '">Book here' + ICON.arrow + '</button>'
            : '<a class="cf-btn cf-btn--book" href="' + esc(c.url || '/pages/locations') + '">View clinic &amp; book' + ICON.arrow + '</a>') +
          (c.url ? '<a class="cf-link" href="' + esc(c.url) + '">' + ICON.info + 'Clinic details</a>' : '') +
          '<a class="cf-link" href="https://maps.google.com/?q=' + encodeURIComponent(c.address || shortName(c.name)) + '" target="_blank" rel="noopener">' + ICON.dir + 'Directions</a>' +
        '</div>';
      li.addEventListener('mouseenter', function () { self.highlight(i, true); });
      li.addEventListener('mouseleave', function () { self.highlight(i, false); });
      frag.appendChild(li);
    });
    e.list.innerHTML = '';
    e.list.appendChild(frag);
    e.list.querySelectorAll('[data-cf-book]').forEach(function (b) {
      b.addEventListener('click', function () { self.book(list[parseInt(b.dataset.cfBook, 10)]); });
    });

    e.more.hidden = this.visible >= list.length;
    e.more.textContent = 'Show more clinics (' + Math.max(0, list.length - this.visible) + ')';

    this.renderMap();
    if (this.visible === this.limit) e.results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  /* ── map (desktop, lazy Leaflet, results only — no pin soup) ── */
  ClinicFinder.prototype.renderMap = function () {
    var self = this, e = this.els;
    if (!DESKTOP_MQ.matches) { e.mapCol.hidden = true; return; }
    e.mapCol.hidden = false;
    var note = e.mapCol.querySelector('.cf-map__note');
    if (note) note.textContent = 'Showing your nearest clinics for this test';
    loadLeaflet(function (L) {
      self.ensureMap(L);
      if (self.dots) self.dots.eachLayer(function (d) { d.setStyle({ fillOpacity: 0.35, weight: 0 }); });
      self.markers.forEach(function (m) { self.map.removeLayer(m); });
      self.markers = [];
      var pts = [];
      var you = L.marker([self.origin.lat, self.origin.lng], { icon: userIcon(L), zIndexOffset: 1000, interactive: false }).addTo(self.map);
      self.markers.push(you); pts.push([self.origin.lat, self.origin.lng]);
      self.results.slice(0, self.visible).forEach(function (c, i) {
        if (isNaN(c.lat) || isNaN(c.lng)) return;
        var m = L.marker([c.lat, c.lng], { icon: pinIcon(L, i + 1, false) }).addTo(self.map);
        m.bindPopup('<strong>' + esc(shortName(c.name)) + '</strong><br>' + esc(c.address) + '<br>' + fmtDist(c.dist));
        m._cfIndex = i;
        self.markers.push(m); pts.push([c.lat, c.lng]);
      });
      setTimeout(function () {
        self.map.invalidateSize();
        self.map.fitBounds(L.latLngBounds(pts), { padding: [28, 28], maxZoom: 12 });
      }, 60);
    });
  };
  ClinicFinder.prototype.highlight = function (i, on) {
    if (!this.map) return;
    this.markers.forEach(function (m) {
      if (m._cfIndex === i && m.setIcon) m.setIcon(pinIcon(window.L, i + 1, on));
    });
  };

  function pinIcon(L, n, hi) {
    return L.divIcon({
      className: 'cf-pin-wrap',
      html: '<span class="cf-pin' + (hi ? ' cf-pin--hi' : '') + '">' + n + '</span>',
      iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -16]
    });
  }
  function userIcon(L) {
    return L.divIcon({ className: 'cf-pin-wrap', html: '<span class="cf-you"></span>', iconSize: [18, 18], iconAnchor: [9, 9] });
  }
  var leafletLoading = null;
  function loadLeaflet(cb) {
    if (window.L && window.L.map) { cb(window.L); return; }
    if (!leafletLoading) {
      leafletLoading = new Promise(function (resolve) {
        if (!document.querySelector('link[href*="leaflet"]')) {
          var css = document.createElement('link'); css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(css);
        }
        var s = document.createElement('script'); s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; s.onload = function () { resolve(window.L); }; document.head.appendChild(s);
      });
    }
    leafletLoading.then(cb);
  }

  /* ── booking hand-off ──
     Writes exactly what bloodtest-variant-change.js (product form) and
     section-book-appointment.js (move_to_step_two) write, then jumps to the
     calendar step with the clinic pre-selected. */
  ClinicFinder.prototype.book = function (c) {
    var d = this.root.dataset, o = this.origin;
    var plans = [];
    try {
      var all = JSON.parse((document.getElementById('main-product-variants') || {}).textContent || '[]');
      var v = all.filter(function (x) { return String(x.id) === String(d.variantId); })[0];
      if (v) plans = v.selling_plans || [];
    } catch (e) { /* leave empty */ }
    var payload = {
      variant_id: d.variantId, variant_sku: d.variantSku, aptId: d.aptId,
      product_handle: d.productHandle, real_product_handle: d.realHandle,
      eurofinsCode: d.eurofinsCode, product_title: d.productTitle,
      main_variant_title: d.variantTitle, main_variant_price: d.variantPrice,
      main_variant_selling_plan_id: d.variantPlanId || '', main_variant_selling_plans: JSON.stringify(plans),
      calendarId: c.calendarId, clinicName: c.name,
      postCode: (o && (o.kind === 'postcode' || o.kind === 'outcode')) ? o.label : '',
      // For the calendar helper (snippets/gb-calendar-assist): the searched location (else the clinic's) + "Back" returns here.
      gb_geo: JSON.stringify({ lat: (o && o.lat) || c.lat, lng: (o && o.lng) || c.lng, cal: String(c.calendarId) }),
      gb_book_from: location.href
    };
    try {
      Object.keys(payload).forEach(function (k) { if (payload[k] != null) sessionStorage.setItem(k, String(payload[k])); });
      var mf = document.getElementById('product-metafields');
      if (mf && mf.dataset.upsells) sessionStorage.setItem('upsells', mf.dataset.upsells);
    } catch (e) { /* storage blocked: fall back to the standard flow */ window.location.href = '/pages/find-clinic'; return; }
    this.track('clinic_finder_book', { clinic: c.name, distance_mi: Math.round(c.dist * 10) / 10 });
    window.location.href = CALENDAR_PAGE;
  };

  /* ── analytics ── */
  ClinicFinder.prototype.track = function (name, data) {
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: name, product: this.root.dataset.realHandle }, data)); } catch (e) { /* ignore */ }
  };
  ClinicFinder.prototype.beacon = function (source) {
    try {
      if (window.Shopify && window.Shopify.designMode) return;
      var o = this.origin, n = this.results[0];
      var q = (o.kind === 'postcode' || o.kind === 'outcode') ? outward(o.label) : o.label;
      var body = JSON.stringify({
        q: q, kind: o.kind, lat: Math.round(o.lat * 100) / 100, lng: Math.round(o.lng * 100) / 100,
        nearest: n ? Math.round(n.dist * 10) / 10 : null, nearestName: n ? n.name : '',
        filter: 'blood', device: DESKTOP_MQ.matches ? 'desktop' : 'mobile',
        src: 'product', product: this.root.dataset.realHandle, source_detail: source
      });
      if (navigator.sendBeacon) navigator.sendBeacon(CAPTURE_URL, body);
      else fetch(CAPTURE_URL, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
    } catch (e) { /* never break the page */ }
  };

  /* ── boot ── */
  function init() {
    document.querySelectorAll('[data-clinic-finder]').forEach(function (root) {
      if (!root._cf) root._cf = new ClinicFinder(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
}());
